import { ApiId, API_IDS, CACHE_CONFIG_TYPES, STORE_NAME } from '@intigral/constant';
import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '@intigral/domain/lib/DataDomain';
import { IChannelSeriesHistoryService } from '@intigral/domain/lib/application/services/ChannelSeriesHistoryService';
import { IChannelService } from '@intigral/domain/lib/application/services/ChannelService';
import { ICmsCarouselService } from '@intigral/domain/lib/application/services/CmsCarouselService';
import { ICustomConfigurationService } from '@intigral/domain/lib/application/services/CustomConfigurationService';
import { IMpxService } from '@intigral/domain/lib/application/services/MpxService';
import { IPopcornService } from '@intigral/domain/lib/application/services/PopcornService';
import { IProfileService } from '@intigral/domain/lib/application/services/ProfileService';
import { IThinkAnalyticsService } from '@intigral/domain/lib/application/services/ThinkAnalyticsService';
import { Carousel } from '@intigral/domain/lib/core/entities/ScreenEntities';
import { IChannelHistoryParams } from '@intigral/domain/lib/entity/interface/iChannelSeriesHistory';
import { IApiPathManager } from '@intigral/domain/lib/infrastructures/config/ApiPathManager';

import { get } from '@intigral/utils/lib/browser-storage/localStorage';
import { getConfiguration } from '../hooks/useConfiguration';
import h, { getCountry } from '../helpers/common';
import user from '../helpers/user';
import { location } from '../interfaces/AppConfiguration';
import { getRewindChannelDataAPi } from '../services/serviceWrapper/rewindServiceWrapper';
import { store } from '../store';
import { miniEpgInitialize } from '../store/reducers/miniEpgSlice';
import { getUserInfo, getSubStatus, getUserTierInfo } from '../utils/user';
import { transformResponseJawwy } from '../utils/transformResponse/jawwyResponse';
import {
    getChannelSchedulesApi,
    getChannelScheduleByDataSource,
} from '../services/serviceWrapper/ChannelsServiceWrapper';
import channelsUtil from '../helpers/channelsUtil';

// REMOVED: updatePromotions moved to React Query configuration
// import { updatePromotions } from '../store/reducers/generalConfigurationSlice';

const getCarouselFeedType = (carousel: Carousel) => {
    return carousel.feedType || carousel.mixpanelCarouselType;
};

// Function to handle specific data stream types
export const handleDataLoaderType = async (carousel: Carousel, track, profile: any, geolocation: location) => {
    /* eslint-disable no-fallthrough */
    switch (getCarouselFeedType(carousel)) {
        // case 'provider':
        // case 'providers':
        //     return await handleGetProviders(carousel, profile.selectedProfile);
        case 'rewind':
            return await handleRewind(carousel);
        case 'most_watched_channels':
            return await handleMostWatchedChannel(carousel, profile.selectedProfile);
        case 'recently_watched_channels':
            return await handleChannelHistory(carousel);
        case 'content_by_genre':
            return carousel.data.content;
        case 'channel_by_categories':
            return carousel.data.content;
        case 'watchlistVod':
            return await handleWatchlistVod(carousel);
        // case 'mpx_biography':
        //     return handleBiography(carousel);
        case 'tv_guide':
            store.dispatch(
                miniEpgInitialize({
                    data_stream: 'jawwy_user_mini_epg',
                    params: { channels_count: '4', refresh_seconds: '30' },
                })
            );
        case 'channels_listings':
            return await handleChannelListing(carousel);
        // case 'custom_configuration':
        //     return handleCustomConfiguration(carousel);
        // case 'catchup':
        //     return handleCatchupList(carousel);
        case 'live_streaming_epg':
            return handleChannelScheduleByDataSourcePath(carousel);
        default:
            return Promise.resolve(null);
    }
    /* eslint-enable no-fallthrough */
};

export const handleBiography = async (carousel: Carousel) => {
    try {
        const data_stream = carousel.dataSources?.[0] ?? '';
        const mpxService = container.resolve<IMpxService>(DI.SERVICES.IMpxService);
        const response = await mpxService.getBiography(data_stream);

        if (!response || !response.entries.length) {
            return [];
        }

        const biographies = response.entries.map((item) => ({
            id: item.id.includes('/') ? item.id.split('/').pop() : item.id,
            guid: item.guid,
            updated: item.updated,
            title: h.getLang() === 'en' ? item.title : item.titleLocalized.ar,
            position: item?.credits,
            image: item.thumbnails.smallThumb?.url?.replace(/^http:\/\//, 'https://') || '',
        }));
        return biographies;
    } catch (error) {
        return null; // Return null on error to avoid breaking the component
    }
};

const handleWatchlistVod = async (carousel: Carousel) => {
    try {
        const carouselDataSource = carousel.dataSources?.[0] ?? '';
        const apiPathManager: IApiPathManager = container.resolve<IApiPathManager>(DI.UTILS.IApiPathManager);
        const url = apiPathManager.replacePlaceholders(carouselDataSource);
        const profileService = container.resolve<IProfileService>(DI.SERVICES.IProfileService);
        const userInfo = getUserInfo();
        const selectedProfile = get(STORE_NAME.SELECTED_PROFILE);

        const result = await profileService.getWatchlist(
            userInfo.apiKey,
            selectedProfile?.profileGuid,
            userInfo.authToken, //userInfo.token,
            userInfo.identityToken,
            {
                country: getCountry(),
                detailed: true,
                device: 'PC',
                tierGuid: userInfo?.tierConfig?.tierGuid,
            }
        );
        return result; // Ensure this returns the exact structure expected by ComponentMapper
    } catch (error) {
        return null; // Return null on error to avoid breaking the component
    }
};

const getLiveStreamChannelAndProgramByChannelId = async (channelId: string) => {
    if (!channelId) return null;
    const currentTime = Date.now();
    const { listings = [] } = (await getChannelSchedulesApi(channelId)) || {};
    const onNowProgram =
        listings.find((prog: any) => currentTime >= prog.startTime && currentTime < prog.endTime) || {};
    const onNowChannel = channelsUtil.getChannelByChannelId(channelId) || {};
    return { ...onNowChannel, ...onNowProgram };
};

const handleChannelScheduleByDataSourcePath = async (carousel: Carousel) => {
    const datasource = carousel?.dataSources[0];
    if (!datasource) return null;
    const data = (await getChannelScheduleByDataSource(datasource)) || null;
    if (data) {
        const { listings = [], channelId } = data || {};
        const onNowChannel = channelsUtil?.getChannelByChannelId(channelId);
        const programList = listings.map((el) => ({
            ...onNowChannel,
            listings: [{ ...el }],
            channelId: onNowChannel?.channelID,
        }));
        return programList;
    }
    return [];
};

// Function to handle promotions
export const handlePromotions = async (carousel: Carousel, localConfig?: any) => {
    // Use provided config or fallback to localStorage for backward compatibility
    const config = localConfig || getConfiguration();
    const dataSources = carousel?.dataSources[0];
    const apiKey = getUserInfo('apiKey');
    const device = h.getDevice();
    const country = getCountry();
    const tierGuid = getUserTierInfo('tierGuid');
    const operator = getUserInfo('operator');
    const subStatus = getSubStatus();
    const productKey = h.getProductKey();
    const range = `0-10`;
    const promoConfig = h.getPromotions();
    const allowExtraSubscriptionGuids =
        (promoConfig && 'allowExtraSubscriptionGuids' in promoConfig && promoConfig.allowExtraSubscriptionGuids) ||
        false;
    const subscriptionGuids = allowExtraSubscriptionGuids
        ? h.getContentFilteredQueryWithSubscriptionsPlusExtraGuids()
        : h.getContentFilteredQueryWithoutDeviceType();
    const popcornService = container.resolve<IPopcornService>(DI.SERVICES.IPopcornService);
    const response = await popcornService.getPromotions(
        apiKey,
        device,
        country,
        subscriptionGuids,
        tierGuid,
        operator,
        subStatus,
        productKey,
        range,
        h.getProviders(),
        h.getLang(),
        config.genreRating,
        dataSources
    );

    const promotions = response.data.promotions;
    const updatedPromotions = await Promise.all(
        promotions.map(async (promo) => {
            if (['LIVESTREAM', 'CHANNEL'].includes(promo.promoType) && promo.properties?.[0]?.channelId) {
                const program = await getLiveStreamChannelAndProgramByChannelId(promo.properties[0].channelId);
                return { ...promo, program };
            } else {
                return promo;
            }
        })
    );

    const data = transformResponseJawwy({ promotions: updatedPromotions });
    // REMOVED: Promotions are now handled by React Query configuration
    // store.dispatch(updatePromotions(data));
    return data;
};

export const handleProfileSelectionPromotions = async (
    apiId: ApiId = API_IDS.POPCORN_API_PROFILE_SELECTION_PROMOTIONS
) => {
    try {
        const apiPathManager: IApiPathManager = container.resolve<IApiPathManager>(DI.UTILS.IApiPathManager);
        const popcornService = container.resolve<IPopcornService>(DI.SERVICES.IPopcornService);
        const promoConfig = h.getPromotions();
        const allowExtraSubscriptionGuids =
            (promoConfig && 'allowExtraSubscriptionGuids' in promoConfig && promoConfig.allowExtraSubscriptionGuids) ||
            false;
        const subscriptionGuids = allowExtraSubscriptionGuids
            ? h.getContentFilteredQueryWithSubscriptionsPlusExtraGuids()
            : h.getContentFilteredQueryWithoutDeviceType();

        const url = apiPathManager.replacePlaceholders(
            apiPathManager.getPath(apiId),
            new Map([
                ['country', getCountry()],
                ['apikey', user.getAPIKey()],
                ['apiKey', user.getAPIKey()],
                ['productKey', h.getProductKey()],
                ['range', '0-10'],
                ['tierGuid', user.getUserActiveTierGuid()],
                ['device', h.getDevice()],
                ['operator', getUserInfo('operator')],
                ['subscriptionGuids', subscriptionGuids],
                ['bySubscriptionGuidsPipe', subscriptionGuids],
            ])
        );

        const popcornResponse = await popcornService.getByPopcornUrl(url);
        return transformResponseJawwy({ promotions: popcornResponse?.data?.promotions ?? [] });
    } catch {
        return [];
    }
};

export const handleGetProviders = async (carousel: Carousel, selectedProfile) => {
    const commonService = container.resolve<ICustomConfigurationService>(DI.SERVICES.ICustomConfigurationService);
    const response = await commonService.getProviders(carousel?.dataSources[0]);
    const filteredData = response?.data_v1
        ? response.data_v1.filter((provider) => provider.enable && (!selectedProfile.kidsMode || provider.kidsMode))
        : response?.data.filter((provider) => !selectedProfile.kidsMode || provider.kidsMode);
    return filteredData;
};

const handleChannelListing = async (carousel: Carousel) => {
    const apiPathManager = container.resolve<IApiPathManager>(DI.UTILS.IApiPathManager);
    const channelListArray = apiPathManager.getParamsByDatasource(carousel?.dataSources[0])?.[0]?.value;
    const channelListString = channelListArray ? channelListArray.join(', ') : '';

    const channelsService = container.resolve<IChannelService>(DI.SERVICES.IChannelService);
    const response = await channelsService.getChannelInfo({
        apiKey: user.getAPIKey(),
        channelList: channelListString,
    });
    return response?.data;
};

const handleRewind = async (carousel: Carousel) => {
    try {
        const apiPathManager: IApiPathManager = container.resolve<IApiPathManager>(DI.UTILS.IApiPathManager);
        const carouselDataSource = apiPathManager.getByDatasource((carousel.dataSources?.[0] as ApiId) ?? null);

        const promoConfig = h.getPromotions();
        const allowExtraSubscriptionGuids =
            (promoConfig && 'allowExtraSubscriptionGuids' in promoConfig && promoConfig.allowExtraSubscriptionGuids) ||
            false;
        const subscriptionGuids = allowExtraSubscriptionGuids
            ? h.getContentFilteredQueryWithSubscriptionsPlusExtraGuids()
            : h.getContentFilteredQueryWithoutDeviceType();

        const placeholders = new Map([
            ['country', getCountry()],
            ['apikey', user.getAPIKey()],
            ['byCategory', 'entertainment'],
            ['productKey', h.getProductKey()],
            ['range', '0-10'],
            ['tierGuid', user.getUserActiveTierGuid()],
            ['country', getCountry()],
            ['device', h.getDevice()],
            ['subscriptionGuids', subscriptionGuids],
            ['bySubscriptionGuidsPipe', subscriptionGuids],
        ]);

        const url = apiPathManager.replacePlaceholders(carouselDataSource, placeholders);

        const popcornService = container.resolve<IPopcornService>(DI.SERVICES.IPopcornService);

        const popcornResponse = await popcornService.getByPopcornUrl(url);
        // TODO: Needs refactoring
        const result = await getRewindChannelDataAPi(popcornResponse);
        // const result = await apis.getJawwyService().rewind().get(popcornResponse);
        return result;
    } catch (error) {
        return null; // Return null on error to avoid breaking the component
    }
};

const handleChannelHistory = async (carousel: Carousel) => {
    const data: IChannelHistoryParams = {
        country: getCountry(),
        device: h.getDevice(),
        deviceType: h.getDeviceType(),
        bySubscriptionGuidsPipeEncoded: h.getContentFilteredQueryWithoutDeviceType(),
    };
    const channelSeriesHistory = container.resolve<IChannelSeriesHistoryService>(
        DI.SERVICES.IChannelSeriesHistoryService
    );
    const response = await channelSeriesHistory.getChannelHistory(
        user.getAPIKey(),
        user.getProfileName(),
        data,
        user.getUserToken(),
        user.getIdentityToken()
    );
    return response;
};

const handleMostWatchedChannel = async (carousel: Carousel, profile) => {
    const taService = container.resolve<IThinkAnalyticsService>(DI.SERVICES.IThinkAnalyticsService);

    const promoConfig = h.getPromotions();
    const allowExtraSubscriptionGuids =
        (promoConfig && 'allowExtraSubscriptionGuids' in promoConfig && promoConfig.allowExtraSubscriptionGuids) ||
        false;
    const subscriptionGuids = allowExtraSubscriptionGuids
        ? h.getContentFilteredQueryWithSubscriptionsPlusExtraGuids()
        : h.getContentFilteredQueryWithoutDeviceType();

    const userInfo = getUserInfo();

    const carouselDataSource = carousel.dataSources?.[0] ?? '';
    const params: Record<string, string | string[]> = {
        apikey: user.getAPIKey(),
        profileName: user.getProfileName(),
        userName: userInfo?.username,
        device: h.getDevice(),
        country: getCountry(),
        bySubscriptionGuidsPipeEncoded: subscriptionGuids,
        categoryType: profile.kidsMode ? 'kids' : '',
    };

    const feedResponse = await taService.getTAByUsecaseId(
        carouselDataSource,
        user.getAPIKey(),
        userInfo?.identityToken,
        params
    );

    return feedResponse?.data ?? [];
};

export const handleCatchupList = async (carousel: Carousel) => {
    const carouselDataSource = carousel.dataSources?.[0] ?? '';
    const cmsCarouselService = container.resolve<ICmsCarouselService>(DI.SERVICES.ICmsCarouselService);
    if (carousel?.active) {
        const response = await cmsCarouselService.getCatchUpCarousel(carouselDataSource, user.getAPIKey(), {
            device: h.getDevice(),
            country: getCountry(),
            bySubscriptionGuidsPipeEncoded: '',
        });
        return response;
    }
    return [];
};
